import { isValidEmail } from '@blpde/nx-ui';
import type { LocationQuery, RouteLocationNormalizedLoaded, Router } from 'vue-router';

/**
 * What the central login hands over when it sends a person on to the hostname
 * of their installation or company (edge rebuild plan W18): the address they
 * typed (`login_hint`) and the company the hostname belongs to (`company`).
 * Both are hints, never a grant: the address only prefills the field (the
 * person still types the password and submits), and master decides after the
 * sign-in whether the account belongs to the company and preselects it.
 *
 * A mailed login link (W18 slice 4) arrives the same way, as `login_link` in
 * the fragment's query (`#/login?login_link=<token>`) — and only there. Unlike
 * the hints it IS a credential — it signs in once — so it is validated by
 * shape, taken out of the address at once and never stored. A `login_link` in
 * the query of the page is not accepted (never read, never redeemed, no
 * message): it would already be in the request line and the access logs.
 */
export interface LoginHints {
  /** A valid address, or ''. */
  email: string;
  /** A well-formed company uuid (lower case), or ''. */
  company: string;
  /** The one-time sign-in token of a mailed login link, or '' (absent or malformed). */
  loginLink: string;
  /** A `login_link` was in the address but is no token: the link was mangled. */
  loginLinkMalformed: boolean;
}

const HINT_PARAMS = ['login_hint', 'company', 'login_link'] as const;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MAX_ADDRESS = 254;
// A login link's token: 256 bits in unpadded base64url (master/loginlinks).
const LOGIN_LINK_TOKEN = /^[A-Za-z0-9_-]{43}$/;

function emailHint(raw: string | null): string {
  const value = (raw ?? '').trim();
  return value && value.length <= MAX_ADDRESS && !/[<>"]/.test(value) && isValidEmail(value)
    ? value
    : '';
}

function companyHint(raw: string | null): string {
  const value = (raw ?? '').trim().toLowerCase();
  return UUID.test(value) ? value : '';
}

function loginLinkToken(raw: string | null): string {
  return raw !== null && LOGIN_LINK_TOKEN.test(raw) ? raw : '';
}

/** The first value of a query parameter of the router's route. */
function routeValue(query: LocationQuery, name: string): string | null {
  const value = query[name];
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === 'string' ? first : null;
}

/**
 * Reads the hints from the address and takes them out of it, so they do not
 * stay in the history or in a copied link. Everything else in the address
 * stays. The values are validated here; what does not pass is dropped.
 *
 * The central login sends them in the query of the page (`/login?login_hint=…`)
 * or of the hash route (`#/login?login_hint=…`). The page's query is taken out
 * with `history.replaceState`. The route's is taken out **through the router**
 * (`router.replace`): Vue Router keeps its own copy of the location in
 * `history.state`, so rewriting only the address bar would leave the hinted
 * location in the entry it restores on Back.
 */
export function takeLoginHints(route: RouteLocationNormalizedLoaded, router: Router): LoginHints {
  const { pathname = '/', search = '', hash = '' } = window.location;
  const page = new URLSearchParams(search);
  const pick = (name: string) => page.get(name) ?? routeValue(route.query, name);
  // Only the fragment's. Master mails fragment links; a token in the query of
  // the page was in the HTTP request line (and any access log) before this ran,
  // so it is taken out of the address below but never read.
  const rawLink = routeValue(route.query, 'login_link');
  const hints: LoginHints = {
    email: emailHint(pick('login_hint')),
    company: companyHint(pick('company')),
    loginLink: loginLinkToken(rawLink),
    loginLinkMalformed: rawLink !== null && !loginLinkToken(rawLink),
  };

  if (HINT_PARAMS.some((name) => page.has(name))) {
    const rest = new URLSearchParams(page);
    for (const name of HINT_PARAMS) rest.delete(name);
    const text = rest.toString();
    window.history.replaceState(window.history.state, '', `${pathname}${text ? `?${text}` : ''}${hash}`);
  }
  if (HINT_PARAMS.some((name) => name in route.query)) {
    const query = { ...route.query };
    for (const name of HINT_PARAMS) delete query[name];
    void router.replace({ path: route.path, query, hash: route.hash });
  }
  return hints;
}
